import { MoneyInputDirective } from '../../shared/ui/money-input.directive';
import { Component, inject, OnInit, signal, computed, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { GameService, TacticalFormation } from '../../core/state/game.service';
import { FORMATION_SLOTS } from '../../core/state/squad.state';
import {
  Player,
  Position,
  LineupSlot,
  PlayerUnavailability,
  getUnavailabilityTypeLabel,
  getUnavailabilityReasonLabel,
  ContractRenewalPayload,
} from '../../core/models/types';
import { CompetitionService } from '../../core/http/competition.service';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { PlayerProfileComponent } from '../../shared/ui/player-profile/player-profile.component';

@Component({
  selector: 'app-squad',
  standalone: true,
  imports: [MoneyInputDirective, CommonModule, FormsModule, IconComponent, RouterLink, PlayerProfileComponent],
  templateUrl: './squad.component.html',
  styleUrl: './squad.component.scss',
  host: {
    '(document:keydown.escape)': 'onEscape()'
  }
})
export class SquadComponent implements OnInit {
  readonly game = inject(GameService);

  private readonly competitionService = inject(CompetitionService);

  constructor() {
    effect(() => {
      const clubId = this.game.userClub()?.id;
      const context = this.competitionService.activeContext();
      const groupId = context.clubId === clubId ? context.clubId : null;
      untracked(() => {
        if (clubId && groupId) {
          void this.game.coaches.loadContracts(clubId, groupId, true);
        } else {
          this.game.coaches.clearState();
        }
      });
    });
  }

  refreshCoach(): void {
    const clubId = this.game.userClub()?.id;
    const context = this.competitionService.activeContext();
    if (clubId && context.clubId === clubId && context.clubId) {
      void this.game.coaches.loadContracts(clubId, context.clubId, true);
    }
  }

  onCoachImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.onerror = null;
      img.src =
        'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 24 24" fill="none" stroke="%2394a3b8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
    }
  }

  get formations(): string[] {
    return this.game.availableFormations();
  }

  isFormationModalOpen = false;
  selectedSlotId: string | null = null;
  sellingPlayer: Player | null = null;
  withdrawingPlayer: Player | null = null;
  viewingPlayer: Player | null = null;
  copiedField: string | null = null;
  askingPriceInput: number | null = 5;
  filterPos = 'ALL';

  readonly isBenchPickerOpen = signal<boolean>(false);

  readonly benchPlayers = computed(() => {
    const ids = this.game.lineupBench();
    const squad = this.game.userSquad();
    return ids
      .map((id) => squad.find((p) => p.playerInstanceId === id || p.id === id))
      .filter((p): p is Player => Boolean(p));
  });

  readonly benchCount = computed(() => this.game.lineupBench().length);

  readonly availableBenchSubstitutes = computed(() => {
    const starterIds = new Set(this.game.lineupSlots().map((s) => s.playerId).filter(Boolean));
    const benchIds = new Set(this.game.lineupBench());
    return this.game.userSquad().filter((p) => {
      const pId = p.id;
      const instId = p.playerInstanceId;
      return (
        !starterIds.has(pId) &&
        (!instId || !starterIds.has(instId)) &&
        !benchIds.has(pId) &&
        (!instId || !benchIds.has(instId))
      );
    });
  });

  getBenchPlayerAt(index: number): Player | null {
    const ids = this.game.lineupBench();
    const id = ids[index];
    if (!id) return null;
    return this.game.userSquad().find((p) => p.playerInstanceId === id || p.id === id) || null;
  }

  isPlayerOnBench(playerId: string | undefined): boolean {
    if (!playerId) return false;
    const bench = this.game.lineupBench();
    return bench.includes(playerId);
  }

  openBenchPicker(): void {
    if (this.benchCount() >= 5) {
      this.game.ui.showNotification('El banquillo admite como máximo 5 suplentes', 'warning');
      return;
    }
    this.isBenchPickerOpen.set(true);
  }

  closeBenchPicker(): void {
    this.isBenchPickerOpen.set(false);
  }

  addBenchSubstitute(playerId: string): void {
    const success = this.game.addBenchPlayer(playerId);
    if (success && this.benchCount() >= 5) {
      this.closeBenchPicker();
    }
  }

  removeBenchSubstitute(playerId: string): void {
    this.game.removeBenchPlayer(playerId);
  }

  toggleBench(playerId: string): void {
    this.game.toggleBenchPlayer(playerId);
  }

  ngOnInit(): void {
    // Cargar formaciones dinámicas desde /api/squads/formations
    this.game.fetchFormations();

    // Si tenemos un club.id seteado (desde /competitions), aseguramos cargar la plantilla real y alineación
    const targetClubId = this.game.activeUserClubId() || this.game.userClub()?.id;
    if (targetClubId && targetClubId !== 'default') {
      this.game.fetchSquadForClub(targetClubId, false).then(() => {
        this.game.fetchClubLineup(targetClubId, false);
      });
    }
  }

  refreshSquad(): void {
    this.refreshCoach();
    this.game.fetchFormations(true);
    const targetClubId = this.game.activeUserClubId() || this.game.userClub()?.id;
    if (targetClubId && targetClubId !== 'default') {
      this.game.fetchSquadForClub(targetClubId, true).then(() => {
        this.game.fetchClubLineup(targetClubId, true);
      });
    }
  }

  openFormationModal(): void {
    if (this.isLocked()) return;
    this.isFormationModalOpen = true;
  }

  closeFormationModal(): void {
    this.isFormationModalOpen = false;
  }

  selectFormationFromModal(formation: string): void {
    if (this.isLocked() || !formation) return;
    this.game.setFormation(formation);
    this.closeFormationModal();
  }

  getFormationBreakdown(formation: string): { por: number; def: number; med: number; del: number } {
    const slots = FORMATION_SLOTS[formation];
    if (!slots) {
      const parts = formation.split('-').map(Number);
      if (parts.length === 3) {
        return { por: 1, def: parts[0] || 4, med: parts[1] || 4, del: parts[2] || 2 };
      }
      if (parts.length === 4) {
        return { por: 1, def: parts[0] || 4, med: (parts[1] || 2) + (parts[2] || 3), del: parts[3] || 1 };
      }
      return { por: 1, def: 4, med: 4, del: 2 };
    }
    return {
      por: slots.filter((s) => s.pos === 'POR').length,
      def: slots.filter((s) => s.pos === 'DEF').length,
      med: slots.filter((s) => s.pos === 'MED').length,
      del: slots.filter((s) => s.pos === 'DEL').length,
    };
  }

  onFormationChange(formation: string): void {
    if (this.isLocked() || !formation) return;
    this.game.setFormation(formation);
  }

  onEscape(): void {
    if (this.sellingPlayer || this.viewingPlayer) return;
    if (this.withdrawingPlayer) {
      this.closeWithdrawalModal();
      return;
    }
    this.isFormationModalOpen = false;
    this.selectedSlotId = null;
  }

  openPlayerDetail(player: Player): void {
    if (player.isAmateur) return;
    this.viewingPlayer = player;
  }

  closePlayerDetail(): void {
    this.viewingPlayer = null;
  }

  copyToClipboard(text: string, fieldName: string): void {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      this.copiedField = fieldName;
      setTimeout(() => {
        if (this.copiedField === fieldName) {
          this.copiedField = null;
        }
      }, 2000);
    }
  }

  openWithdrawalModal(player: Player): void {
    if (!this.game.transferPending().has(player.id)) this.withdrawingPlayer = player;
  }

  closeWithdrawalModal(): void {
    if (this.withdrawingPlayer && !this.game.transferPending().has(this.withdrawingPlayer.id)) {
      this.withdrawingPlayer = null;
    }
  }

  async confirmWithdrawal(): Promise<void> {
    const player = this.withdrawingPlayer;
    if (!player || this.game.transferPending().has(player.id)) return;
    const removed = await this.game.removePlayerFromSale(player.id);
    if (removed) {
      if (this.viewingPlayer?.id === player.id) {
        this.viewingPlayer = { ...this.viewingPlayer, listedForTransfer: false, askingPrice: undefined, listingId: undefined };
      }
      this.withdrawingPlayer = null;
    }
  }

  // Persistence state
  readonly isSaving = signal<boolean>(false);
  readonly saveStatus = signal<'idle' | 'saving' | 'saved' | 'error'>('idle');
  readonly saveErrorMessage = signal<string | null>(null);

  isLocked(): boolean {
    try {
      const md = this.game.currentMatchday?.();
      return !!md && (md.status === 'LOCKED' || md.status === 'SCORED');
    } catch {
      return false;
    }
  }

  async saveLineup(): Promise<void> {
    this.saveErrorMessage.set(null);

    if (this.isLocked()) {
      this.saveStatus.set('error');
      this.saveErrorMessage.set('La jornada está bloqueada (LOCKED). No se pueden guardar cambios.');
      return;
    }

    const count = this.assignedCount();
    if (count !== 11) {
      this.saveStatus.set('error');
      this.saveErrorMessage.set(`Debes alinear exactamente 11 futbolistas antes de guardar (actualmente: ${count}/11).`);
      return;
    }

    if (this.benchCount() > 5) {
      this.saveStatus.set('error');
      this.saveErrorMessage.set('El banquillo puede tener como máximo 5 suplentes.');
      return;
    }

    this.isSaving.set(true);
    this.saveStatus.set('saving');

    try {
      const res = await this.game.saveLineup();
      this.isSaving.set(false);

      if (res.success) {
        this.saveStatus.set('saved');
        this.saveErrorMessage.set(null);
        setTimeout(() => {
          if (this.saveStatus() === 'saved') {
            this.saveStatus.set('idle');
          }
        }, 3000);
      } else {
        this.saveStatus.set('error');
        this.saveErrorMessage.set(res.error || 'Error al guardar la alineación y el banquillo');
      }
    } catch (err: any) {
      this.isSaving.set(false);
      this.saveStatus.set('error');
      this.saveErrorMessage.set(err?.message || 'Error inesperado al guardar la alineación y el banquillo');
    }
  }

  assignedCount(): number {
    return this.game.lineupSlots().filter((s) => s.playerId !== null).length;
  }

  starterMetrics() {
    const starterIds = new Set(this.game.lineupSlots().map(slot => slot.playerId));
    const players = this.game.userSquad().filter(player => starterIds.has(player.id));
    const sumPoints = (select: (player: Player) => number | null | undefined) => {
      const values = players.map(select).filter((value): value is number => value != null && Number.isFinite(value));
      return values.length ? values.reduce((total, value) => total + value, 0) : null;
    };
    return {
      value: players.reduce((total, player) => total + (Number.isFinite(player.currentMarketValue) ? player.currentMarketValue : 0), 0),
      global: sumPoints(player => player.averagePoints),
      form: sumPoints(player => player.last3AveragePoints),
    };
  }

  averageStarterRating(): number {
    const slots = this.game.lineupSlots();
    const squad = this.game.userSquad();
    const count = this.assignedCount();
    if (count === 0) return 0;
    const total = slots
      .map((s) => squad.find((p) => p.id === s.playerId)?.overall || 0)
      .reduce((a, b) => a + b, 0);
    return Math.round(total / count);
  }

  getPlayerInSlot(playerId: string | null): Player | undefined {
    if (!playerId) return undefined;
    return this.game.userSquad().find((p) => p.id === playerId);
  }

  selectedSlot(): LineupSlot | undefined {
    return this.game.lineupSlots().find((s) => s.slotId === this.selectedSlotId);
  }

  getStarterSlot(playerId: string): LineupSlot | undefined {
    return this.game.lineupSlots().find((s) => s.playerId === playerId);
  }

  getStarterSlotLabel(playerId: string): string | null {
    const slot = this.getStarterSlot(playerId);
    return slot ? slot.label : null;
  }

  isMatchingSlotPosition(player: Player): boolean {
    const slot = this.selectedSlot();
    if (!slot) return false;
    const targetPos = this.game.squad.mapBackendPosition(slot.position);
    const playerPos = this.game.squad.mapBackendPosition(player.position);
    return playerPos === targetPos || !!player.secondaryPositions?.some((p) => this.game.squad.mapBackendPosition(p) === targetPos);
  }

  shouldShowOtherPositionsDivider(cand: Player, index: number, candidates: Player[]): boolean {
    const slot = this.selectedSlot();
    if (!slot || index === 0) return false;
    const targetPos = this.game.squad.mapBackendPosition(slot.position);
    const candPos = this.game.squad.mapBackendPosition(cand.position);
    const candMatch = candPos === targetPos || !!cand.secondaryPositions?.some((p) => this.game.squad.mapBackendPosition(p) === targetPos);
    if (candMatch) return false;
    const prev = candidates[index - 1];
    if (!prev) return false;
    const prevPos = this.game.squad.mapBackendPosition(prev.position);
    const prevMatch = prevPos === targetPos || !!prev.secondaryPositions?.some((p) => this.game.squad.mapBackendPosition(p) === targetPos);
    return prevMatch;
  }

  private sortCandidates(players: Player[]): Player[] {
    const slot = this.selectedSlot();
    if (!slot) return [...players];

    const targetPos = this.game.squad.mapBackendPosition(slot.position);

    return [...players].sort((a, b) => {
      const aPos = this.game.squad.mapBackendPosition(a.position);
      const bPos = this.game.squad.mapBackendPosition(b.position);

      const aDirectMatch = aPos === targetPos ? 2 : (a.secondaryPositions?.some((p) => this.game.squad.mapBackendPosition(p) === targetPos) ? 1 : 0);
      const bDirectMatch = bPos === targetPos ? 2 : (b.secondaryPositions?.some((p) => this.game.squad.mapBackendPosition(p) === targetPos) ? 1 : 0);

      // 1. Mostrar primero los del tipo de la posición que se ha seleccionado
      if (aDirectMatch !== bDirectMatch) {
        return bDirectMatch - aDirectMatch;
      }

      // Si ninguno coincide con la posición objetivo, agrupar por orden táctico (POR, DEF, MED, DEL)
      if (aDirectMatch === 0 && bDirectMatch === 0 && aPos !== bPos) {
        const order: Record<Position, number> = { POR: 1, DEF: 2, MED: 3, DEL: 4 };
        return (order[aPos] || 99) - (order[bPos] || 99);
      }

      // 2. Ordenar por puntuación media / valoración global
      const aRating = a.overall ?? (a.averagePoints ? a.averagePoints * 10 : 0);
      const bRating = b.overall ?? (b.averagePoints ? b.averagePoints * 10 : 0);
      if (bRating !== aRating) {
        return bRating - aRating;
      }

      // 3. Valor de mercado
      const aVal = a.currentMarketValue ?? a.contract?.releaseClause ?? 0;
      const bVal = b.currentMarketValue ?? b.contract?.releaseClause ?? 0;
      if (bVal !== aVal) {
        return bVal - aVal;
      }

      return (a.name || '').localeCompare(b.name || '');
    });
  }

  candidatePlayers(): Player[] {
    const squad = (this.game.userSquad() || []).filter((p) => !p.isAmateur);
    return this.sortCandidates(squad);
  }

  candidateAmateurPlayers(): Player[] {
    const squad = (this.game.userSquad() || []).filter((p) => !!p.isAmateur);
    return this.sortCandidates(squad);
  }

  handleSlotClick(slot: LineupSlot) {
    if (this.isLocked()) return;
    this.selectedSlotId = slot.slotId;
  }

  assignPlayer(playerId: string) {
    if (!this.selectedSlotId) return;
    this.game.assignPlayerToSlot(this.selectedSlotId, playerId);
    this.selectedSlotId = null;
  }

  unassignCurrentSlot() {
    if (!this.selectedSlotId) return;
    this.game.assignPlayerToSlot(this.selectedSlotId, null);
    this.selectedSlotId = null;
  }

  isPlayerStarter(playerId: string): boolean {
    return this.game.lineupSlots().some((s) => s.playerId === playerId);
  }

  filteredSquad(): Player[] {
    const squad = this.game.userSquad();
    if (this.filterPos === 'ALL') return squad;
    return squad.filter((p) => p.position === this.filterPos);
  }

  professionalSquad(): Player[] {
    return this.filteredSquad().filter(player => !player.isAmateur);
  }

  amateurSquad(): Player[] {
    return this.filteredSquad().filter(player => player.isAmateur);
  }

  getPositionLabel(pos: string): string {
    const labels: Record<string, Position> = {
      GOALKEEPER: 'POR',
      DEFENDER: 'DEF',
      MIDFIELDER: 'MED',
      FORWARD: 'DEL',
    };
    return labels[pos] ?? pos;
  }

  getPositionColor(pos: Position | string): string {
    switch (pos) {
      case 'POR':
        return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/40';
      case 'DEF':
        return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/40';
      case 'MED':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/40';
      case 'DEL':
        return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/20 dark:text-rose-300 dark:border-rose-500/40';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
    }
  }

  openSellModal(player: Player) {
    if (this.game.transferPending().has(player.id)) return;
    this.sellingPlayer = player;
    this.askingPriceInput = player.contract.releaseClause || 5;
  }

  async confirmListing() {
    if (!this.sellingPlayer) return;
    const playerId = this.sellingPlayer.id;
    const saved = await this.game.listPlayerForSale(playerId, this.askingPriceInput ?? 0);
    if (saved) {
      if (this.viewingPlayer?.id === playerId) {
        this.viewingPlayer = this.game.allPlayers().find(p => p.id === playerId) || this.viewingPlayer;
      }
      if (this.sellingPlayer?.id === playerId) this.sellingPlayer = null;
    }
  }

  getFullPositionName(pos: Position | string): string {
    switch (pos) {
      case 'POR':
      case 'GOALKEEPER':
        return 'Portero';
      case 'DEF':
      case 'DEFENDER':
        return 'Defensa';
      case 'MED':
      case 'MIDFIELDER':
        return 'Centrocampista';
      case 'DEL':
      case 'FORWARD':
        return 'Delantero';
      default:
        return String(pos || 'Jugador');
    }
  }

  removePlayerFromLineup(playerId: string): void {
    const slot = this.game.lineupSlots().find((s) => s.playerId === playerId);
    if (slot) {
      this.game.assignPlayerToSlot(slot.slotId, null);
    }
  }

  findSlotForPlayer(player: Player): void {
    const slots = this.game.lineupSlots();
    const matchingEmpty = slots.find(
      (s) => s.position === player.position && s.playerId === null
    );
    if (matchingEmpty) {
      this.game.assignPlayerToSlot(matchingEmpty.slotId, player.id);
      this.closePlayerDetail();
      return;
    }
    const anyEmpty = slots.find((s) => s.playerId === null);
    if (anyEmpty) {
      this.game.assignPlayerToSlot(anyEmpty.slotId, player.id);
      this.closePlayerDetail();
      return;
    }
    this.closePlayerDetail();
  }

  readonly viewMode = signal<'cards' | 'table'>('cards');

  getPlayerClubShortName(player: Player | undefined | null): string | null {
    if (!player) return null;
    if (player.realClub?.shortName) {
      return player.realClub.shortName;
    }
    if (player.clubShortName) {
      return player.clubShortName;
    }
    if (player.clubId) {
      const found = this.game.clubs().find((c) => c.id === player.clubId);
      return found?.code || null;
    }
    return null;
  }

  getPlayerClubName(player: Player | undefined | null): string {
    if (!player) return '';
    if (player.realClub?.name) {
      return player.realClub.name;
    }
    if (player.clubName) {
      return player.clubName;
    }
    if (player.clubId) {
      const found = this.game.clubs().find((c) => c.id === player.clubId);
      return found ? found.name : 'Club';
    }
    return 'Sin Club';
  }

  getPlayerCrestUrl(player: Player | undefined | null): string | null {
    const shortName = this.getPlayerClubShortName(player);
    if (!shortName) return null;
    return `assets/crest/${shortName.trim().toUpperCase()}.png`;
  }

  onCrestError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.style.display = 'none';
    }
  }

  hasUnavailability(player: Player | undefined | null): boolean {
    return !!(player?.unavailabilities && player.unavailabilities.length > 0);
  }

  getPrimaryUnavailability(player: Player | undefined | null): PlayerUnavailability | undefined {
    return player?.unavailabilities?.[0];
  }

  getUnavailabilityTypeLabel(type?: string | null): string {
    return getUnavailabilityTypeLabel(type);
  }

  getUnavailabilityReasonLabel(reason?: string | null): string {
    return getUnavailabilityReasonLabel(reason);
  }

  getUnavailabilityBadgeClass(type?: string | null): string {
    const t = (type || '').toUpperCase();
    if (t === 'INJURY' || t === 'LESIÓN' || t === 'LESION') {
      return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-900/60';
    }
    if (t === 'SANCTION' || t === 'SANCION' || t === 'SUSPENSION' || t === 'CARD') {
      return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-900/60';
    }
    return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
  }

  getUnavailabilityIcon(type?: string | null): string {
    const t = (type || '').toUpperCase();
    if (t === 'INJURY' || t === 'LESIÓN' || t === 'LESION') {
      return 'activity';
    }
    return 'alert-circle';
  }

  formatPoints(pts?: number | null): string {
    if (pts === null || pts === undefined || Number.isNaN(pts)) {
      return '-';
    }
    return pts.toFixed(2);
  }

  formatMarketValue(val?: number | null): string {
    if (val === null || val === undefined || Number.isNaN(val)) {
      return '0.0 M€';
    }
    return `${val.toFixed(1)} M€`;
  }

  // --- RENOVACIÓN DE CONTRATOS ---
  renewingPlayer = signal<Player | null>(null);
  showRenewalModal = signal<boolean>(false);
  renewalAnnualSalaryM = 2.0;
  readonly renewalContractYears = 1; // En las renovaciones siempre será 1 temporada
  renewalReleaseClauseM = 25.0;
  isSubmittingRenewal = signal<boolean>(false);
  renewalModalError = signal<string | null>(null);

  openRenewalModal(player: Player): void {
    if (player.isAmateur) {
      this.game.showNotification('Los jugadores amateurs no disponen de contrato profesional para renovar.', 'info');
      return;
    }
    this.renewingPlayer.set(player);
    const currSalary = player.contract?.salary && player.contract.salary > 0
      ? player.contract.salary
      : Math.max(0.5, (player.currentMarketValue || 5.0) * 0.15);

    const currClause = player.contract?.releaseClause && player.contract.releaseClause > 0
      ? (player.contract.releaseClause > 100_000 ? player.contract.releaseClause / 1_000_000 : player.contract.releaseClause)
      : Math.max(10.0, (player.currentMarketValue || 5.0) * 2.5);

    this.renewalAnnualSalaryM = Math.max(0.5, Math.round(currSalary * 1.25 * 10) / 10);
    this.renewalReleaseClauseM = Math.max(Math.round(currClause * 1.3 * 10) / 10, currClause + 5);
    this.renewalModalError.set(null);
    this.showRenewalModal.set(true);
  }

  closeRenewalModal(): void {
    this.showRenewalModal.set(false);
    this.renewingPlayer.set(null);
    this.renewalModalError.set(null);
    this.isSubmittingRenewal.set(false);
  }

  async confirmRenewal(): Promise<void> {
    const player = this.renewingPlayer();
    if (!player) return;

    if (this.renewalAnnualSalaryM <= 0) {
      this.renewalModalError.set('La nueva ficha anual debe ser superior a 0.');
      return;
    }

    const currentClauseRaw = player.contract?.releaseClause ?? 0;
    const currentClauseM = currentClauseRaw > 100_000 ? currentClauseRaw / 1_000_000 : currentClauseRaw;
    if (this.renewalReleaseClauseM < currentClauseM) {
      this.renewalModalError.set('La nueva cláusula de rescisión debe ser igual o superior a la cláusula actual.');
      return;
    }

    this.isSubmittingRenewal.set(true);
    this.renewalModalError.set(null);

    const payload: ContractRenewalPayload = {
      playerId: player.playerInstanceId || player.id,
      annualSalary: Math.round(this.renewalAnnualSalaryM * 100_000_000) / 100,
      termSeasons: 1,
      releaseClause: Math.round(this.renewalReleaseClauseM * 100_000_000) / 100,
    };

    try {
      const response = await this.game.submitContractRenewal(payload);
      if (response && (response.status === 'PENDING' || response.status === 'ACCEPTED')) {
        this.game.showNotification(
          response.status === 'ACCEPTED' ? `La renovación de ${player.name} ya se ha completado.` : `Propuesta de renovación presentada para ${player.name}. La resolución se llevará a cabo en el cierre diario.`,
          'success'
        );
        this.closeRenewalModal();
      }
    } catch (err: any) {
      console.error('Error al proponer renovación:', err);
      // Criterio 11: Se muestra el error sin perder los datos introducidos
      this.renewalModalError.set(err?.message || 'No se pudo presentar la renovación de contrato.');
    } finally {
      this.isSubmittingRenewal.set(false);
    }
  }
}
